import { Menu } from '@highlight/ui'
import { AttachmentMenuItem, AttachmentMenuItemType, AttachmentSubmenu } from './attachments-menu'
import { useCallback, useMemo } from 'react'
import { useAttachments, useAttachmentStore } from '../../../../../store'
import { AudioNote } from '@highlight/common'
import { AudioNoteIcon, Timestamp, useAudioNoteTitle } from '../audio/audio-elements'
import { useTrackAddAttachment, useTrackRemoveAttachment } from '../../../../../hooks/attachments-hooks'
import { useAudioNotes } from '../../../../../hooks/audio-notes'

export const AudioMenuItem = (item: AttachmentMenuItemType & { onClick?: (e: React.MouseEvent) => void }) => {
  const { data } = useAudioNotes()

  const conversations = useMemo(() => {
    return Array.isArray(data) ? (data as AudioNote[]) : ([] as AudioNote[])
  }, [data])

  return (
    <AttachmentSubmenu item={item}>
      {conversations.length === 0 && (
        <AttachmentMenuItem
          id={'not-available'}
          label={<span className={'leading-normal'}>No audio notes available</span>}
          disabled
          onClick={() => {}}
        />
      )}
      {conversations.map((conversation) => {
        return <AudioNoteItem key={conversation.id} {...conversation} />
      })}
      {conversations.length > 5 && (
        <>
          <Menu.Separator />
          <AttachmentMenuItem
            id={'view-all'}
            label={'View All'}
            onClick={() => window.open('highlight://app/highlightchat', '_blank')}
          />
        </>
      )}
    </AttachmentSubmenu>
  )
}

const AudioNoteItem = (conversation: AudioNote & { onClick?: (e: React.MouseEvent) => void }) => {
  const { addAudioNoteAttachment, removeAttachment } = useAttachmentStore()
  const attachments = useAttachments()
  const trackAddAttachment = useTrackAddAttachment()
  const trackRemoveAttachment = useTrackRemoveAttachment()

  const title = useAudioNoteTitle(conversation)

  const attachmentIndex = useMemo(() => {
    return attachments.findIndex(
      (a) => a.attachment?.type === 'audioNote' && (a.metadata as AudioNote).id === conversation.id,
    )
  }, [attachments, conversation])

  const onClick = useCallback<React.MouseEventHandler>(
    (e) => {
      e.stopPropagation()
      if (typeof conversation.onClick === 'function') {
        conversation.onClick(e)
      }
      if (attachmentIndex === -1) {
        addAudioNoteAttachment(conversation, { reportMissingTranscript: true })
        trackAddAttachment({ type: 'audioNote', trigger: 'Attachments Menu' })
      } else {
        removeAttachment(attachmentIndex)
        trackRemoveAttachment({ type: 'audioNote', trigger: 'Attachments Menu' })
      }
    },
    [conversation, addAudioNoteAttachment, removeAttachment, trackAddAttachment, trackRemoveAttachment],
  )

  return (
    <AttachmentMenuItem
      id={conversation.id}
      icon={<AudioNoteIcon size={14} {...conversation} />}
      label={title}
      description={<Timestamp date={conversation.startedAt} />}
      selectable={true}
      selected={attachmentIndex !== -1}
      onClick={onClick}
    />
  )
}
